import React from "react";

import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { Bold, Italic, Strikethrough, Underline } from "lucide-react";

import { useInlineContentEditor } from "./hooks/useInlineContentEditor";

type InlineToolbarProps = {
  className?: string;
};

/**
 * An inline toolbar component for text editing with formatting options.
 * Provides buttons for bold, italic, underline, strikethrough formatting.
 * Text is automatically saved on blur, so no save button is needed.
 */
function InlineToolbar({ className }: InlineToolbarProps) {
  const {
    currentFormatting,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrikethrough,
  } = useInlineContentEditor();

  // TODO (Kurt, 2025-08-05): Remove the hidden class when we re-enable the toolbar.
  return (
    <div
      className={twMerge(
        "hidden items-center gap-2 bg-white border border-border rounded-lg shadow-sm p-2",
        className,
      )}
    >
      {/* Formatting buttons */}
      <IconButton
        variant="tertiary"
        size="sm"
        icon={<Bold className="w-4 h-4" />}
        onClick={toggleBold}
        isActive={currentFormatting.bold}
        tooltipText="Bold"
      />
      <IconButton
        variant="tertiary"
        size="sm"
        icon={<Italic className="w-4 h-4" />}
        onClick={toggleItalic}
        isActive={currentFormatting.italic}
        tooltipText="Italic"
      />
      <IconButton
        variant="tertiary"
        size="sm"
        icon={<Underline className="w-4 h-4" />}
        onClick={toggleUnderline}
        isActive={currentFormatting.underline}
        tooltipText="Underline"
      />
      <IconButton
        variant="tertiary"
        size="sm"
        icon={<Strikethrough className="w-4 h-4" />}
        onClick={toggleStrikethrough}
        isActive={currentFormatting.strikethrough}
        tooltipText="Strikethrough"
      />
    </div>
  );
}

export default InlineToolbar;
